/**
 * OnboardingWizard - Multi-step onboarding flow for new users
 * 
 * Features:
 * - Welcome screen
 * - Profile completion
 * - Preferences setup
 * - Interactive feature tour
 * - Team invitation (admin only)
 * - Integration setup
 * - Completion celebration
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { api } from '../config/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import {
  ChevronRight, 
  ChevronLeft, 
  Check, 
  X, 
  User,
  Settings,
  Compass,
  Users,
  Link2,
  PartyPopper,
  Sun,
  Moon,
  Monitor,
  Bell,
  Mail,
  Calendar,
  LayoutDashboard,
  Bot,
  FileText,
  ShieldCheck,
  Building2,
  BarChart3,
  AlertTriangle,
  Plus,
  Trash2,
  ExternalLink
} from 'lucide-react';

// Step icons mapping
const STEP_ICONS = {
  welcome: Check,
  profile: User,
  preferences: Settings,
  feature_tour: Compass,
  team_setup: Users,
  integrations: Link2,
  complete: PartyPopper
};

// Feature icons mapping
const FEATURE_ICONS = {
  LayoutDashboard,
  Bot,
  FileText,
  Calendar,
  ShieldCheck,
  Building2,
  BarChart3,
  AlertTriangle,
  Users
};

function OnboardingWizard({ onComplete, onSkip }) {
  const { darkMode, setDarkMode } = useTheme();
  const navigate = useNavigate();
  
  // Wizard state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [steps, setSteps] = useState([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [stepData, setStepData] = useState({});
  
  // Feature tour state
  const [features, setFeatures] = useState([]);
  const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0);
  
  // Form states
  const [profileData, setProfileData] = useState({
    name: '',
    department: '',
    job_role: 'hybrid',
    title: '',
    phone: ''
  });
  
  const [preferencesData, setPreferencesData] = useState({
    theme: 'system',
    notifications_email: true,
    notifications_in_app: true,
    notifications_deadline_reminders: true,
    notifications_team_updates: true,
    default_view: 'dashboard'
  });
  
  const [teamEmails, setTeamEmails] = useState(['']);
  const [teamMessage, setTeamMessage] = useState('');
  
  const [integrationData, setIntegrationData] = useState({
    sam_gov_api_key: '',
    connect_email: false,
    connect_calendar: false
  });

  // Load onboarding status
  const loadOnboardingStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/onboarding/status');
      
      if (response.onboarding_complete) {
        onComplete?.();
        return;
      }
      
      setSteps(response.steps || []);
      setCurrentStepIndex(response.progress?.current_step || 0);
      setCompletedSteps(response.progress?.completed_steps || []);
      setStepData(response.step_data || {});
      
      // Pre-fill profile data from saved data
      if (response.step_data?.profile) {
        setProfileData(prev => ({ ...prev, ...response.step_data.profile }));
      }
      if (response.step_data?.preferences) {
        setPreferencesData(prev => ({ ...prev, ...response.step_data.preferences }));
      }
      
      // Load feature highlights
      const featuresResponse = await api.get('/onboarding/feature-highlights');
      setFeatures(featuresResponse.features || []);
      
    } catch (error) {
      logger.error('Failed to load onboarding status:', error);
    } finally {
      setLoading(false);
    }
  }, [onComplete]);

  useEffect(() => {
    loadOnboardingStatus();
  }, [loadOnboardingStatus]);

  // Get current step
  const currentStep = steps[currentStepIndex] || {};
  const StepIcon = STEP_ICONS[currentStep.id] || Check;
  const progress = steps.length > 0 ? ((currentStepIndex + 1) / steps.length) * 100 : 0;

  // Track step start for analytics
  const trackStepStart = async (stepId) => {
    try {
      await api.post(`/onboarding/track-step-start?step_id=${stepId}`);
    } catch (error) {
      // Silently fail - analytics shouldn't block user flow
      logger.debug('Analytics tracking failed:', error);
    }
  };

  // Track step start when step changes
  useEffect(() => {
    if (currentStep?.id) {
      trackStepStart(currentStep.id);
    }
  }, [currentStepIndex, currentStep?.id]);

  // Navigation handlers
  const goToNextStep = async () => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    }
  };

  const goToPreviousStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  };

  // Save step data
  const saveStepData = async (stepId, data) => {
    try {
      setSaving(true);
      await api.post(`/onboarding/step/${stepId}`, data);
      setCompletedSteps(prev => [...new Set([...prev, stepId])]);
      return true;
    } catch (error) {
      logger.error('Failed to save step:', error);
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Handle step completion
  const handleStepComplete = async () => {
    const stepId = currentStep.id;
    let data = {};
    
    switch (stepId) {
      case 'profile':
        data = profileData;
        break;
      case 'preferences':
        data = preferencesData;
        // Apply theme immediately
        if (preferencesData.theme === 'dark') {
          setDarkMode(true);
        } else if (preferencesData.theme === 'light') {
          setDarkMode(false);
        }
        break;
      case 'team_setup':
        // Team invites handled separately
        break;
      case 'integrations':
        data = integrationData;
        break;
      default:
        break;
    }
    
    const success = await saveStepData(stepId, data);
    if (success) {
      goToNextStep();
    }
  };

  // Handle wizard completion
  const handleComplete = async () => {
    try {
      setSaving(true);
      await api.post('/onboarding/complete');
      onComplete?.();
    } catch (error) {
      logger.error('Failed to complete onboarding:', error);
    } finally {
      setSaving(false);
    }
  };

  // Handle skip
  const handleSkip = async () => {
    try {
      setSaving(true);
      await api.post('/onboarding/skip');
      onSkip?.();
    } catch (error) {
      logger.error('Failed to skip onboarding:', error);
    } finally {
      setSaving(false);
    }
  };

  // Handle team invite
  const handleTeamInvite = async () => {
    const validEmails = teamEmails.filter(e => e.trim() && e.includes('@'));
    if (validEmails.length === 0) {
      goToNextStep();
      return;
    }
    
    try {
      setSaving(true);
      await api.post('/onboarding/team/invite', {
        emails: validEmails,
        role: 'user',
        message: teamMessage
      });
      await saveStepData('team_setup', { invited: validEmails });
      goToNextStep();
    } catch (error) {
      logger.error('Failed to send invites:', error);
    } finally {
      setSaving(false);
    }
  };

  // Add email field for team invites
  const addEmailField = () => {
    if (teamEmails.length < 10) {
      setTeamEmails([...teamEmails, '']);
    }
  };

  // Remove email field
  const removeEmailField = (index) => {
    setTeamEmails(teamEmails.filter((_, i) => i !== index));
  };

  // Update email field
  const updateEmail = (index, value) => {
    const newEmails = [...teamEmails];
    newEmails[index] = value;
    setTeamEmails(newEmails);
  };

  if (loading) {
    return (
      <div className={`fixed inset-0 z-50 flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
        <div className="text-center">
          <Check className={`w-12 h-12 mx-auto mb-4 animate-pulse ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
          <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Loading...</p>
        </div>
      </div>
    );
  }

  // Render step content
  const renderStepContent = () => {
    switch (currentStep.id) {
      case 'welcome':
        return <WelcomeStep darkMode={darkMode} />;
      
      case 'profile':
        return (
          <ProfileStep 
            darkMode={darkMode}
            data={profileData}
            onChange={setProfileData}
          />
        );
      
      case 'preferences':
        return (
          <PreferencesStep 
            darkMode={darkMode}
            data={preferencesData}
            onChange={setPreferencesData}
          />
        );
      
      case 'feature_tour':
        return (
          <FeatureTourStep 
            darkMode={darkMode}
            features={features}
            currentIndex={currentFeatureIndex}
            onIndexChange={setCurrentFeatureIndex}
          />
        );
      
      case 'team_setup':
        return (
          <TeamSetupStep 
            darkMode={darkMode}
            emails={teamEmails}
            message={teamMessage}
            onAddEmail={addEmailField}
            onRemoveEmail={removeEmailField}
            onUpdateEmail={updateEmail}
            onMessageChange={setTeamMessage}
          />
        );
      
      case 'integrations':
        return (
          <IntegrationsStep 
            darkMode={darkMode}
            data={integrationData}
            onChange={setIntegrationData}
          />
        );
      
      case 'complete':
        return <CompleteStep darkMode={darkMode} />;
      
      default:
        return null;
    }
  };

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${darkMode ? 'bg-gray-900/95' : 'bg-white/95'} backdrop-blur-sm`}>
      <div className={`w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
        {/* Progress bar */}
        <div className="px-6 pt-4">
          <Progress value={progress} className="h-1.5" />
          <div className="flex justify-between mt-2 text-xs">
            <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
              Step {currentStepIndex + 1} of {steps.length}
            </span>
            <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
              {Math.round(progress)}% complete
            </span>
          </div>
        </div>

        {/* Step indicators */}
        <div className="flex justify-center gap-2 py-4 px-6">
          {steps.map((step, index) => {
            const Icon = STEP_ICONS[step.id] || Check;
            const isActive = index === currentStepIndex;
            const isCompleted = completedSteps.includes(step.id);
            
            return (
              <div 
                key={step.id}
                className={`flex items-center justify-center w-10 h-10 rounded-full transition-all ${
                  isActive 
                    ? 'bg-blue-600 text-white scale-110' 
                    : isCompleted 
                      ? (darkMode ? 'bg-green-600/20 text-green-400' : 'bg-green-100 text-green-600')
                      : (darkMode ? 'bg-gray-700 text-gray-500' : 'bg-gray-100 text-gray-400')
                }`}
              >
                {isCompleted && !isActive ? (
                  <Check className="w-5 h-5" />
                ) : (
                  <Icon className="w-5 h-5" />
                )}
              </div>
            );
          })}
        </div>

        {/* Header */}
        <div className="text-center px-6 pb-4">
          <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {currentStep.title}
          </h2>
          <p className={`mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {currentStep.description}
          </p>
        </div>

        {/* Content */}
        <div className={`px-6 py-4 min-h-[300px] max-h-[400px] overflow-y-auto ${darkMode ? 'bg-gray-800/50' : 'bg-gray-50/50'}`}>
          {renderStepContent()}
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-between px-6 py-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div>
            {currentStepIndex > 0 && currentStep.id !== 'complete' && (
              <Button
                variant="ghost"
                onClick={goToPreviousStep}
                disabled={saving}
                className={darkMode ? 'text-gray-300 hover:text-white' : ''}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {!currentStep.required && currentStep.id !== 'complete' && (
              <Button
                variant="ghost"
                onClick={handleSkip}
                disabled={saving}
                className={darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500'}
              >
                Skip for now
              </Button>
            )}
            
            {currentStep.id === 'complete' ? (
              <Button
                onClick={handleComplete}
                disabled={saving}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
              >
                {saving ? 'Finishing...' : 'Get Started'}
                <Check className="w-4 h-4 ml-2" />
              </Button>
            ) : currentStep.id === 'team_setup' ? (
              <Button
                onClick={handleTeamInvite}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {saving ? 'Sending...' : teamEmails.some(e => e.trim()) ? 'Send Invites & Continue' : 'Skip & Continue'}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={handleStepComplete}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {saving ? 'Saving...' : 'Continue'}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== STEP COMPONENTS ====================

function WelcomeStep({ darkMode }) {
  return (
    <div className="text-center py-8">
      <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
        <Check className="w-12 h-12 text-white" />
      </div>
      <h3 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        Welcome to AcuSightPro! 
      </h3>
      <p className={`max-w-md mx-auto ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
        Your Federal Acquisition Intelligence Platform. Let's take a few minutes to set up your 
        account and show you around.
      </p>
      <div className="mt-8 grid grid-cols-3 gap-4 max-w-sm mx-auto">
        {[
          { icon: FileText, label: 'Smart Documents' },
          { icon: Bot, label: 'AI Assistant' },
          { icon: ShieldCheck, label: 'Compliance' }
        ].map((item, i) => (
          <div key={i} className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-white'}`}>
            <item.icon className={`w-6 h-6 mx-auto mb-1 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProfileStep({ darkMode, data, onChange }) {
  const departments = [
    'Acquisitions',
    'Contracting',
    'Program Management',
    'Finance',
    'Legal',
    'IT',
    'Other'
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="name" className={darkMode ? 'text-gray-300' : ''}>Full Name</Label>
          <Input
            id="name"
            value={data.name}
            onChange={(e) => onChange({ ...data, name: e.target.value })}
            placeholder="John Smith"
            className={darkMode ? 'bg-gray-700 border-gray-600 text-white' : ''}
          />
        </div>
        <div>
          <Label htmlFor="title" className={darkMode ? 'text-gray-300' : ''}>Job Title</Label>
          <Input
            id="title"
            value={data.title}
            onChange={(e) => onChange({ ...data, title: e.target.value })}
            placeholder="Contracting Specialist"
            className={darkMode ? 'bg-gray-700 border-gray-600 text-white' : ''}
          />
        </div>
      </div>
      
      <div>
        <Label htmlFor="department" className={darkMode ? 'text-gray-300' : ''}>Department</Label>
        <select
          id="department"
          value={data.department}
          onChange={(e) => onChange({ ...data, department: e.target.value })}
          className={`w-full px-3 py-2 rounded-md border ${
            darkMode 
              ? 'bg-gray-700 border-gray-600 text-white' 
              : 'bg-white border-gray-300 text-gray-900'
          }`}
        >
          <option value="">Select department...</option>
          {departments.map(dept => (
            <option key={dept} value={dept}>{dept}</option>
          ))}
        </select>
      </div>
      
      <div>
        <Label className={darkMode ? 'text-gray-300' : ''}>Primary Role</Label>
        <RadioGroup 
          value={data.job_role} 
          onValueChange={(value) => onChange({ ...data, job_role: value })}
          className="mt-2"
        >
          <div className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
            data.job_role === 'co' 
              ? (darkMode ? 'bg-blue-900/30 border-blue-500' : 'bg-blue-50 border-blue-500')
              : (darkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300')
          }`}>
            <RadioGroupItem value="co" id="role-co" />
            <Label htmlFor="role-co" className="cursor-pointer flex-1">
              <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Contracting Officer</span>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Focus on compliance, clauses, and vendor management</p>
            </Label>
          </div>
          <div className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
            data.job_role === 'pm' 
              ? (darkMode ? 'bg-blue-900/30 border-blue-500' : 'bg-blue-50 border-blue-500')
              : (darkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300')
          }`}>
            <RadioGroupItem value="pm" id="role-pm" />
            <Label htmlFor="role-pm" className="cursor-pointer flex-1">
              <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Program Manager</span>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Focus on analytics, reporting, and team oversight</p>
            </Label>
          </div>
          <div className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
            data.job_role === 'hybrid' 
              ? (darkMode ? 'bg-blue-900/30 border-blue-500' : 'bg-blue-50 border-blue-500')
              : (darkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300')
          }`}>
            <RadioGroupItem value="hybrid" id="role-hybrid" />
            <Label htmlFor="role-hybrid" className="cursor-pointer flex-1">
              <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Hybrid / Other</span>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Access to all features and views</p>
            </Label>
          </div>
        </RadioGroup>
      </div>
      
      <div>
        <Label htmlFor="phone" className={darkMode ? 'text-gray-300' : ''}>Phone (optional)</Label>
        <Input
          id="phone"
          type="tel"
          value={data.phone}
          onChange={(e) => onChange({ ...data, phone: e.target.value })}
          placeholder="(555) 123-4567"
          className={darkMode ? 'bg-gray-700 border-gray-600 text-white' : ''}
        />
      </div>
    </div>
  );
}

function PreferencesStep({ darkMode, data, onChange }) {
  return (
    <div className="space-y-6">
      {/* Theme */}
      <div>
        <Label className={`text-base font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          Theme Preference
        </Label>
        <div className="grid grid-cols-3 gap-3 mt-3">
          {[
            { id: 'light', icon: Sun, label: 'Light' },
            { id: 'dark', icon: Moon, label: 'Dark' },
            { id: 'system', icon: Monitor, label: 'System' }
          ].map(theme => (
            <button
              key={theme.id}
              onClick={() => onChange({ ...data, theme: theme.id })}
              className={`p-4 rounded-lg border-2 transition-all ${
                data.theme === theme.id
                  ? 'border-blue-500 bg-blue-500/10'
                  : (darkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300')
              }`}
            >
              <theme.icon className={`w-6 h-6 mx-auto mb-2 ${
                data.theme === theme.id 
                  ? 'text-blue-500' 
                  : (darkMode ? 'text-gray-400' : 'text-gray-500')
              }`} />
              <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{theme.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Notifications */}
      <div>
        <Label className={`text-base font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          Notification Preferences
        </Label>
        <div className="space-y-3 mt-3">
          {[
            { key: 'notifications_email', icon: Mail, label: 'Email notifications', desc: 'Receive updates via email' },
            { key: 'notifications_in_app', icon: Bell, label: 'In-app notifications', desc: 'Show notifications in the app' },
            { key: 'notifications_deadline_reminders', icon: Calendar, label: 'Deadline reminders', desc: 'Get reminded about upcoming deadlines' },
            { key: 'notifications_team_updates', icon: Users, label: 'Team updates', desc: 'Notify about team activity' }
          ].map(notification => (
            <div 
              key={notification.key}
              className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}
            >
              <div className="flex items-center gap-3">
                <notification.icon className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                <div>
                  <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{notification.label}</p>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{notification.desc}</p>
                </div>
              </div>
              <Switch
                checked={data[notification.key]}
                onCheckedChange={(checked) => onChange({ ...data, [notification.key]: checked })}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Default view */}
      <div>
        <Label className={`text-base font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          Default Landing Page
        </Label>
        <RadioGroup 
          value={data.default_view} 
          onValueChange={(value) => onChange({ ...data, default_view: value })}
          className="mt-3 grid grid-cols-3 gap-3"
        >
          {[
            { value: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
            { value: 'projects', icon: FileText, label: 'Projects' },
            { value: 'calendar', icon: Calendar, label: 'Calendar' }
          ].map(view => (
            <div 
              key={view.value}
              className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer ${
                data.default_view === view.value
                  ? 'border-blue-500 bg-blue-500/10'
                  : (darkMode ? 'border-gray-600' : 'border-gray-200')
              }`}
              onClick={() => onChange({ ...data, default_view: view.value })}
            >
              <RadioGroupItem value={view.value} id={`view-${view.value}`} />
              <view.icon className={`w-4 h-4 ${data.default_view === view.value ? 'text-blue-500' : (darkMode ? 'text-gray-400' : 'text-gray-500')}`} />
              <Label htmlFor={`view-${view.value}`} className="cursor-pointer">
                <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{view.label}</span>
              </Label>
            </div>
          ))}
        </RadioGroup>
      </div>
    </div>
  );
}

function FeatureTourStep({ darkMode, features, currentIndex, onIndexChange }) {
  const currentFeature = features[currentIndex];
  const FeatureIcon = currentFeature ? (FEATURE_ICONS[currentFeature.icon] || Check) : Check;

  if (!currentFeature) {
    return (
      <div className="text-center py-8">
        <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Loading features...</p>
      </div>
    );
  }

  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    purple: 'from-purple-500 to-purple-600',
    green: 'from-green-500 to-green-600',
    amber: 'from-amber-500 to-amber-600',
    emerald: 'from-emerald-500 to-emerald-600',
    indigo: 'from-indigo-500 to-indigo-600',
    cyan: 'from-cyan-500 to-cyan-600',
    red: 'from-red-500 to-red-600',
    pink: 'from-pink-500 to-pink-600'
  };

  return (
    <div className="text-center">
      {/* Feature display */}
      <div className={`w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br ${colorClasses[currentFeature.highlight_color] || colorClasses.blue} flex items-center justify-center shadow-lg`}>
        <FeatureIcon className="w-10 h-10 text-white" />
      </div>
      
      <h3 className={`text-xl font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        {currentFeature.title}
      </h3>
      <p className={`max-w-md mx-auto mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
        {currentFeature.description}
      </p>
      
      {/* Navigation dots */}
      <div className="flex justify-center gap-2 mb-6">
        {features.map((_, index) => (
          <button
            key={index}
            onClick={() => onIndexChange(index)}
            className={`w-2.5 h-2.5 rounded-full transition-all ${
              index === currentIndex 
                ? 'bg-blue-500 w-6' 
                : (darkMode ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-300 hover:bg-gray-400')
            }`}
          />
        ))}
      </div>
      
      {/* Navigation buttons */}
      <div className="flex justify-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onIndexChange(Math.max(0, currentIndex - 1))}
          disabled={currentIndex === 0}
          className={darkMode ? 'border-gray-600 text-gray-300' : ''}
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Previous
        </Button>
        
        {currentIndex < features.length - 1 ? (
          <Button
            size="sm"
            onClick={() => onIndexChange(currentIndex + 1)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Next Feature
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Badge variant="outline" className="px-4 py-2 text-green-600 border-green-500">
            <Check className="w-4 h-4 mr-1" />
            Tour Complete!
          </Badge>
        )}
      </div>
    </div>
  );
}

function TeamSetupStep({ darkMode, emails, message, onAddEmail, onRemoveEmail, onUpdateEmail, onMessageChange }) {
  return (
    <div className="space-y-4">
      <div className={`p-4 rounded-lg ${darkMode ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-200'}`}>
        <p className={`text-sm ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
          <Users className="w-4 h-4 inline mr-2" />
          Invite colleagues to collaborate on procurement projects. They'll receive an email invitation.
        </p>
      </div>
      
      <div>
        <Label className={darkMode ? 'text-gray-300' : ''}>Team Member Emails</Label>
        <div className="space-y-2 mt-2">
          {emails.map((email, index) => (
            <div key={index} className="flex gap-2">
              <Input
                type="email"
                value={email}
                onChange={(e) => onUpdateEmail(index, e.target.value)}
                placeholder="colleague@va.gov"
                className={darkMode ? 'bg-gray-700 border-gray-600 text-white' : ''}
              />
              {emails.length > 1 && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onRemoveEmail(index)}
                  className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
        
        {emails.length < 10 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onAddEmail}
            className={`mt-2 ${darkMode ? 'text-gray-400 hover:text-gray-300' : ''}`}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add another email
          </Button>
        )}
      </div>
      
      <div>
        <Label className={darkMode ? 'text-gray-300' : ''}>Personal Message (optional)</Label>
        <Textarea
          value={message}
          onChange={(e) => onMessageChange(e.target.value)}
          placeholder="Hey team, I'm setting up AcuSightPro for our procurement workflow..."
          rows={3}
          className={`mt-2 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
        />
      </div>
    </div>
  );
}

function IntegrationsStep({ darkMode, data, onChange }) {
  return (
    <div className="space-y-4">
      <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
        <div className="flex items-start gap-3">
          <Building2 className={`w-6 h-6 mt-0.5 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
          <div className="flex-1">
            <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>SAM.gov Integration</h4>
            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Connect your SAM.gov API key to verify vendors and access entity data.
            </p>
            <Input
              type="password"
              value={data.sam_gov_api_key}
              onChange={(e) => onChange({ ...data, sam_gov_api_key: e.target.value })}
              placeholder="Enter SAM.gov API key"
              className={`mt-3 ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : ''}`}
            />
            <a 
              href="https://api.sam.gov/" 
              target="_blank" 
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-1 text-sm mt-2 ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
            >
              Get an API key <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>
      
      <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mail className={`w-6 h-6 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
            <div>
              <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Email Integration</h4>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Sync with Microsoft 365 / Outlook
              </p>
            </div>
          </div>
          <Switch
            checked={data.connect_email}
            onCheckedChange={(checked) => onChange({ ...data, connect_email: checked })}
          />
        </div>
      </div>
      
      <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calendar className={`w-6 h-6 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} />
            <div>
              <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Calendar Integration</h4>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Sync deadlines with your calendar
              </p>
            </div>
          </div>
          <Switch
            checked={data.connect_calendar}
            onCheckedChange={(checked) => onChange({ ...data, connect_calendar: checked })}
          />
        </div>
      </div>
      
      <p className={`text-sm text-center ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
        You can configure more integrations later in Settings → Integrations
      </p>
    </div>
  );
}

function CompleteStep({ darkMode }) {
  return (
    <div className="text-center py-8">
      <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center animate-bounce">
        <PartyPopper className="w-12 h-12 text-white" />
      </div>
      <h3 className={`text-2xl font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        You're All Set!
      </h3>
      <p className={`max-w-md mx-auto mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
        Your account is ready. Start exploring AcuSightPro and streamline your federal acquisition workflow.
      </p>
      
      <div className={`p-4 rounded-lg inline-block ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Need help? Press <kbd className={`px-2 py-0.5 rounded ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>?</kbd> anytime to access the Help Center
        </p>
      </div>
    </div>
  );
}

export default OnboardingWizard;
